import React, { useState } from 'react';
import { User, Bot, Clock, Copy, Check } from 'lucide-react';
import { MessageProps } from './types';

const Message: React.FC<MessageProps> = ({ 
  message, 
  isLatest = false, 
  showAvatar = false 
}) => {
  const isUser = message.role === 'user';
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      // Include both question and answer for assistant messages
      let textToCopy = message.content;
      if (!isUser && message.question) {
        textToCopy = `Q: ${message.question}\n\nA: ${message.content}`;
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const handleQuestionClick = (question: string) => {
    // Create a custom event to send the question to the chat interface
    const event = new CustomEvent('blp-question-click', { 
      detail: { question: question.replace(/^["']|["']$/g, '') } // Remove quotes
    });
    window.dispatchEvent(event);
  };

  // Format BLP responses with proper markdown-like rendering
  const formatBLPContent = (content: string) => {
    // Split content by the BLP format sections, but keep more structure
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let currentIndex = 0;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines
      if (!line) continue;
      
      // Handle bold headers - more flexible matching for markdown bold formatting
      if (line.includes('**') && (line.startsWith('**') || line.match(/\*\*[^*]+\*\*/))) {
        // Extract text between ** markers, handling cases with trailing text
        const boldMatch = line.match(/\*\*([^*]+)\*\*/);
        if (boldMatch) {
          const headerText = boldMatch[1].trim();
          // Check if this is a BLP section header
          const isBLPHeader = headerText.includes('ACKNOWLEDGE') || 
                              headerText.includes('BLP SOLUTION') || 
                              headerText.includes('NEXT STEP') || 
                              headerText.includes('CLARIFY');
          
          if (isBLPHeader) {
            // Add the header
            elements.push(
              <div 
                key={currentIndex++} 
                className={`font-semibold text-primary-200 ${elements.length > 0 ? 'mt-4' : 'mt-0'}`}
              >
                {headerText}
              </div>
            );
            
            // Check if there's content after the header on the same line
            const afterHeader = line.substring(line.indexOf('**', 2) + 2).trim();
            if (afterHeader) {
              elements.push(
                <div key={currentIndex++} className="leading-relaxed mt-2">
                  {afterHeader}
                </div>
              );
            }
            continue;
          }
        }
      }
      
      // Handle [ASK] and [PROSPECT] questions
      if (line.includes('[ASK]:') || line.includes('[PROSPECT]:')) {
        const [label, ...questionParts] = line.split(':');
        const question = questionParts.join(':').trim();
        const isProspectQuestion = label.includes('[PROSPECT]');
        
        elements.push(
          <div key={currentIndex++} className="mt-2">
            <span className="text-green-300 font-medium">{label}:</span>
            {isProspectQuestion ? (
              <button
                onClick={() => handleQuestionClick(question)}
                className="ml-2 italic text-light-secondary hover:text-primary-300 cursor-pointer 
                         underline decoration-dotted underline-offset-2 hover:decoration-solid
                         transition-all duration-200 hover:bg-primary-500/10 px-1 py-0.5 rounded"
                title="Click to ask this question"
              >
                {question}
              </button>
            ) : (
              <span className="ml-2 italic text-light-secondary">{question}</span>
            )}
          </div>
        );
        continue;
      }
      
      // Regular content lines
      if (line) {
        elements.push(
          <div key={currentIndex++} className="leading-relaxed">
            {line}
          </div>
        );
      }
    }
    
    return elements;
  };

  return (
    <div className={`flex gap-4 ${isUser ? 'flex-row-reverse' : 'flex-row'} group max-w-none`}>
      {/* Avatar */}
      {showAvatar && (
        <div className={`
          flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
          ${isUser 
            ? 'bg-primary-500 text-white' 
            : 'bg-dark-tertiary text-light-secondary border border-dark-tertiary/50'
          }
        `}>
          {isUser ? <User size={16} /> : <Bot size={16} />}
        </div>
      )}

      {/* Message Content */}
      <div className={`
        flex flex-col w-full
        ${isUser ? 'items-end' : 'items-start'}
      `}>
        {/* Message Bubble */}
        <div 
          className={`
            relative group/message w-full max-w-none
            ${isUser 
              ? 'px-6 py-4 bg-primary-500/20 rounded-2xl border border-primary-500/30' 
              : 'px-6 py-5 bg-dark-tertiary/30 rounded-2xl border border-dark-tertiary/30'
            }
            ${isLatest ? 'animate-fade-in' : ''}
            transition-all duration-200 hover:border-primary-500/40
          `}
        >
          {/* Copy button for assistant messages */}
          {!isUser && (
            <div className="absolute top-3 right-3">
              <button
                onClick={handleCopy}
                className={`
                  p-1.5 rounded-lg transition-all duration-200
                  ${copied 
                    ? 'bg-green-500/20 text-green-400 opacity-100' 
                    : 'bg-dark-secondary/50 hover:bg-dark-secondary/70 text-light-tertiary hover:text-light-primary opacity-0 group-hover/message:opacity-100'
                  }
                `}
                title={copied ? 'Copied to clipboard!' : 'Copy message with question'}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
              
              {/* Tooltip for copy feedback */}
              {copied && (
                <div className="absolute -top-8 right-0 bg-dark-primary border border-primary-500/30 
                             text-green-400 text-xs px-2 py-1 rounded whitespace-nowrap z-10
                             animate-fade-in">
                  Copied!
                </div>
              )}
            </div>
          )}

          <div className="text-sm text-light-primary pr-8">
            {isUser ? (
              <div className="leading-relaxed">{message.content}</div>
            ) : (
              <div className="blp-formatted-content">
                {formatBLPContent(message.content)}
              </div>
            )}
          </div>
        </div>

        {/* Timestamp and Status */}
        <div className={`
          flex items-center gap-2 mt-2 px-2 text-xs text-light-tertiary
          ${isUser ? 'flex-row-reverse' : 'flex-row'}
          opacity-0 group-hover:opacity-100 transition-opacity
        `}>
          <Clock size={12} />
          <span>
            {message.timestamp.toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            })}
          </span>
          {!isUser && (
            <span className="text-green-400">
              • BLP Assistant
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Message;